import React, { useContext, useEffect } from 'react';

import { CREDIT_ACCOUNT_STATEMENT_FORM_URL } from '../components/PaymentTermsContainer';
import { CreditAccountConstantProps, CreditAccountDynamicProps, OpenModalTypes } from '../types';
import { getDueAmountOrBalanceInCents } from '../utils';

export enum DateTermValueType {
  DAYS_FROM_TODAY = 'days_from_today',
  SPECIFIC_DATE = 'specific_date',
}

export enum AmountTermValueType {
  PERCENTAGE = 'percentage',
  FIXED_AMOUNT = 'fixed_amount',
}

export enum DueAmountFormats {
  CENTS = 'CENTS',
  DECIMAL = 'DECIMAL',
}

export type PaymentTermContextType = {
  setGlobalState: React.Dispatch<React.SetStateAction<GlobalState>>;
  constantProps: CreditAccountConstantProps;
  globalState: GlobalState;
  getDueAmount: (format?: DueAmountFormats) => number; // The current due amount based on the amount found in the statement. Might be based on a configured percentage, or just the entire due amount, based on the amount term value type.
  getDueDate: () => string;
  getDefaultAmountDue: () => string;
  getDefaultDueDate: () => string;
};

export const PaymentTermContext = React.createContext<PaymentTermContextType | undefined>(
  undefined
);

export type GlobalState = CreditAccountDynamicProps & {
  openModal: OpenModalTypes;
  hasLightSpeedPayments: boolean;
  dateTermValue: string;
  amountTermValue: string;
  dateTermValueType: DateTermValueType;
  amountTermValueType: AmountTermValueType;
  totalAmountOwed: number;
  isFetchingOwedAmount: boolean;
  includePaymentLink: boolean;
};

/**
 * Formats a date object to a string as YYYY-MM-DD, offset by the number of days provided.
 *
 * @param daysTillDue - The number of days to offset the date by.
 * @returns string - The date in the format of YYYY-MM-DD in UTC.
 */
export const formatDate = (daysTillDue: number): string => {
  const localDateTime = new Date();
  localDateTime.setDate(localDateTime.getDate() + daysTillDue);
  const [month, day, year] = localDateTime
    .toLocaleDateString('en', { day: '2-digit', month: '2-digit', year: 'numeric' })
    .split('/');
  return `${year}-${month}-${day}`;
};

export const PaymentTermsContextProvider = ({
  children,
  constantProps,
  dynamicProps,
}: {
  children?: React.ReactNode;
  constantProps: CreditAccountConstantProps;
  dynamicProps: CreditAccountDynamicProps;
}) => {
  /**
   * Gets the default value for the global state's due date attribute.
   *
   * @returns string - The default date term value. If a value is configured, use that. Otherwise, default to 15 days from today.
   */
  const getDefaultGlobalStateDueDate = (): string => {
    // Stored as a string, will be empty if no value is provided.
    // Converted to integer here to add to the current date.
    let daysTillDue = parseInt(constantProps.billingOptions.daysTillDue, 10);
    if (Number.isNaN(daysTillDue)) {
      daysTillDue = 15;
    }

    return formatDate(daysTillDue);
  };

  /**
   * Gets the default value for the global states' amount due attribute.
   *
   * @returns string - The configured default percentage due.
   */
  const getDefaultGlobalStatePercentageDue = (): number => {
    // Always a numeric value. Defaults to 0 is completely removed in general options.
    // Stored as a string in the format of a decimal number (e.g. '.5' = 50%)
    // For 100%, stored as a whole number (e.g. '1' = 100%).
    return parseFloat(constantProps.billingOptions.percentageDue) * 100;
  };

  const [globalState, setGlobalState] = React.useState<GlobalState>({
    ...dynamicProps,
    openModal: 'none',
    hasLightSpeedPayments: false,
    dateTermValue: getDefaultGlobalStateDueDate(),
    amountTermValue: getDefaultGlobalStatePercentageDue().toString(),
    dateTermValueType: DateTermValueType.SPECIFIC_DATE,
    amountTermValueType: AmountTermValueType.PERCENTAGE,
    totalAmountOwed: 0,
    isFetchingOwedAmount: false,
    includePaymentLink: false,
  });

  const statementPeriodBalanceUrl = `${CREDIT_ACCOUNT_STATEMENT_FORM_URL}&credit_account_id=${constantProps.creditAccount.id}&start=${globalState.startDate}&end=${globalState.endDate}`;

  // Fetch the total amount owed for the current period when the context is mounted to avoid the use of async functions
  // being returned from the context.
  useEffect(() => {
    const fetchDueAmount = async () => {
      setGlobalState((previousState) => ({ ...previousState, isFetchingOwedAmount: true }));
      const balanceInCents = await getDueAmountOrBalanceInCents({
        balanceCheckUrl: statementPeriodBalanceUrl,
        option: 'balance',
      });

      if (balanceInCents !== undefined) {
        // Keep as scaled value (Cents).
        const parseBalanceInCents = parseInt(balanceInCents, 10);
        setGlobalState((previousState) => {
          const {
            amountTermValueType: prevAmountTermValueType,
            amountTermValue: prevAmountTermValue,
          } = previousState;

          return {
            ...previousState,
            totalAmountOwed: parseBalanceInCents,
            amountTermValue:
              prevAmountTermValueType === AmountTermValueType.FIXED_AMOUNT
                ? (parseBalanceInCents / 100).toString()
                : prevAmountTermValue,
            isFetchingOwedAmount: false,
          };
        });
      } else {
        setGlobalState((previousState) => ({
          ...previousState,
          totalAmountOwed: 0,
          isFetchingOwedAmount: false,
        }));
      }
    };

    fetchDueAmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementPeriodBalanceUrl]);

  /**
   * Gets the due date of the payment in the string format of YYYY-MM-DD. This value is normalized to UTC
   * for consistency across any location. No conversion from local time to UTC is actually done, we just use the local
   * year, month, day. So if the local time is 2021-08-31, the UTC date will be 2021-08-31
   *
   * Used to reset the values when the input type changes.
   *
   * @return string - The due date in the format of YYYY-MM-DD if a specific date is needed, otherwise, the number of days from today.
   */
  const getDefaultDueDate = (): string => {
    if (globalState.dateTermValueType === DateTermValueType.SPECIFIC_DATE) {
      return getDefaultGlobalStateDueDate().toString();
    }

    // Stored as a string, will be empty if no value is provided.
    // Converted to integer here to add to the current date.
    let daysTillDue = constantProps.billingOptions.daysTillDue;
    if (daysTillDue === '') {
      daysTillDue = '15';
    }

    return daysTillDue;
  };

  /**
   * Gets the default amount due. This will always be 50% of the amount owed unless
   * otherwise configured for percentage amounts. Used for display purposes.
   *
   * Used to reset the values when the input type changes.
   *
   * @returns string - The default amount due, dependant on the amount term value type.
   *                   If it is a percentage, base it on the configured value.
   *                   Otherwise, default values are always 50% of the owed amount during the current period
   *                   determined by the context's dynamicProps.startDate and dynamicProps.endDate.
   */
  const getDefaultAmountDue = (): string => {
    if (globalState.amountTermValueType === AmountTermValueType.PERCENTAGE) {
      return getDefaultGlobalStatePercentageDue().toString();
    }

    const dueAmount = globalState.totalAmountOwed;
    return `${dueAmount / 100}`;
  };

  /**
   * Gets the total amount due. This value is determined by the set period.
   * If there is a percentage configured/set via input, calculate the value. Otherwise, return the total amount owed
   * extracted from the statement.
   * Used for calculating the real due amount based on all inputs.
   *
   * @returns number - Gets the amount due based on the total amount owed.
   */
  const getDueAmount = (format: DueAmountFormats = DueAmountFormats.DECIMAL): number => {
    let totalOwed = globalState.totalAmountOwed / 100;
    let amountTermValue = parseFloat(globalState.amountTermValue);
    if (globalState.amountTermValueType === AmountTermValueType.PERCENTAGE) {
      // Ceil will have different behaviors when the value is negative or positive.
      // Do calculations as a positive value only for consistency, then re-apply the sign.
      let negativeValue = false;
      if (amountTermValue < 0) {
        negativeValue = true;
        amountTermValue = -amountTermValue;
      }

      amountTermValue /= 100; // convert to percentage from a whole number.

      // Handle uneven money division. E.g. $5.97 owed, but only 50% payment needed. 50% of 5.97 is 2.985, that half a cent should be rounded up or down.
      // So we are not missing pennies between two separate payments.
      totalOwed = Math.round(totalOwed * amountTermValue * 100);

      if (negativeValue) {
        totalOwed = -totalOwed;
      }

      if (format === DueAmountFormats.DECIMAL) {
        return totalOwed / 100;
      }
    } else {
      totalOwed = amountTermValue;
      if (format === DueAmountFormats.CENTS) {
        return Math.round(totalOwed * 100);
      }
    }

    return totalOwed;
  };

  /**
   *
   * Gets the due date in the string format of YYYY-MM-DD based on the input of dateTermValue.
   *
   * @returns string - The due date as YYYY-MM-DD
   */
  const getDueDate = (): string => {
    const dueDate = globalState.dateTermValue;
    if (globalState.dateTermValueType === DateTermValueType.SPECIFIC_DATE) {
      return dueDate;
    }

    let daysTillDue = parseInt(dueDate, 10);
    if (Number.isNaN(daysTillDue)) {
      daysTillDue = 0;
    }

    return formatDate(daysTillDue);
  };

  return (
    <PaymentTermContext.Provider
      value={{
        setGlobalState,
        constantProps,
        globalState,
        getDueAmount,
        getDueDate,
        getDefaultAmountDue,
        getDefaultDueDate,
      }}
    >
      {children}
    </PaymentTermContext.Provider>
  );
};

export const usePaymentTermsInputContext = () => {
  const context = useContext(PaymentTermContext);

  if (!context) {
    throw new Error(
      'usePaymentTermsInputContext must be used inside the PaymentTermsContextProvider'
    );
  }

  return context;
};
